/**
 * useInstallPrompt — Shared hook for PWA install detection.
 *
 * Returns whether the app is running as an installed PWA,
 * whether the browser's install prompt is available,
 * and a function to trigger the install prompt.
 */

import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isPwa, setIsPwa] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    setIsPwa(isStandalone());
    setIsIos(isIOS());

    if (isStandalone()) return;

    function handleBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
    return outcome === 'accepted';
  }, [deferredPrompt]);

  return {
    /** True if app is running in standalone / installed PWA mode */
    isPwa,
    /** True if the browser install prompt is available (Chrome/Edge) */
    canInstall: !!deferredPrompt,
    /** True if the device is iOS (needs manual install instructions) */
    isIos: isIos && !isPwa,
    /** Trigger the browser's native install prompt */
    triggerInstall,
  };
}
